import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Modal, Checkbox, Select } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import { CloseOutlined, SearchOutlined } from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import { fetchUsersByAny } from "@/store/redux/slices/user_admin/user_admin";
import { getOrganizationId } from "@/utils/token_functions";
import { createSelector } from "@reduxjs/toolkit";

const MemberCard = ({ member, isSelected = false, onSelect }) => {
  return (
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-center gap-4">
        <Avatar src={member.profileImage} size="large" shape="circle" />
        <div className="flex flex-col">
          <span className="text-regular font-bold text-black">
            {member.fullname || member.username}
          </span>
          <span className="text-small text-gray">{member.email}</span>
        </div>
      </div>
      <Checkbox checked={isSelected} onChange={() => onSelect(member._id)} />
    </div>
  );
};

MemberCard.propTypes = {
  member: PropTypes.shape({
    _id: PropTypes.string,
    fullname: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    profileImage: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

// Create memoized selector
const selectUserAdminState = createSelector(
  (state) => state.userAdmin,
  (userAdmin) => ({
    users: userAdmin.fetchedUsers || [],
    isLoading: userAdmin.isLoading,
    total: userAdmin.total,
  })
);

const InviteMembersDialog = ({
  isVisible,
  onClose,
  onMembersSelected,
  modalTitle = "Add New User",
  primaryHeader = "Invited Members",
  showSecondaryHeader = true,
  secondaryHeader = "Other Members",
  roleOptions = [],
  invitedMembers = [],
  emptyInvitedMessage = "No members have been invited yet.",
  onUpdateMember,
  firstSectionContent = "invited",
}) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const limit = 5; // Number of users to load at once
  const { showLoader, hideLoader } = useLoader();

  // Use memoized selector
  const { users, isLoading, total } = useSelector(selectUserAdminState);

  // Initialize users when dialog opens
  useEffect(() => {
    if (isVisible) {
      setOffset(0);
      setDisplayedUsers([]);

      const organizationID = getOrganizationId();
      if (organizationID) {
        dispatch(
          fetchUsersByAny({
            key: "organizationID",
            value: organizationID,
            pagination: { skip: 0, limit },
          })
        );
      }
    }
  }, [isVisible, dispatch, limit]);

  // Update displayed users when users from API change
  useEffect(() => {
    if (users.length > 0) {
      setDisplayedUsers((prevUsers) => {
        const existingIds = new Set(prevUsers.map((user) => user._id));
        const newUsers = users.filter((user) => !existingIds.has(user._id));
        return [...prevUsers, ...newUsers];
      });
    }
  }, [users]);

  const loadMoreUsers = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);

    const organizationID = getOrganizationId();
    if (organizationID) {
      dispatch(
        fetchUsersByAny({
          key: "organizationID",
          value: organizationID,
          pagination: { skip: newOffset, limit },
        })
      );
    }
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleProceed = () => {
    if (selectedMembers.length > 0) {
      const selectedMembersData = getSelectedMembersDetails();
      onClose();
      showLoader();

      setTimeout(() => {
        hideLoader();
        onMembersSelected(selectedMembersData);
      }, 2000);
    }
  };

  const getSelectedMembersDetails = () => {
    return users
      .filter((user) => selectedMembers.includes(user._id))
      .map((user) => ({
        id: user._id,
        name: user.fullname || user.username,
        email: user.email,
        profileImage: user.profileImage,
        avatar: user.profileImage, // keep for backward compatibility
        role: user.role,
      }));
  };

  // Filter users based on search query
  const filteredUsers = displayedUsers.filter((user) => {
    const fullname = user.fullname || "";
    const username = user.username || "";
    const email = user.email || "";

    return (
      fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Filter out already invited members from the second section
  const filteredAvailableUsers = users.filter(
    (user) => !invitedMembers.some((member) => member.id === user._id)
  );

  const hasMoreUsers = displayedUsers.length < total;

  const capitalizeRole = (role) => {
    if (!role) return "";
    return role
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      closeIcon={null}
      styles={{
        body: { padding: 0 },
        content: {
          padding: 0,
          borderRadius: "16px",
          overflow: "hidden",
        },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
      }}>
      <div className="flex flex-col bg-white">
        {/* Header */}
        <div className="p-4 px-6 flex justify-between items-center">
          <h2 className="text-bold-x text-primary">{modalTitle}</h2>
          <CloseOutlined
            onClick={onClose}
            className="cursor-pointer"
            style={{
              color: "var(--veegil-color-primary)",
              backgroundColor: "var(--veegil-color-secondary)",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>

        {/* Content Area */}
        <div className="flex flex-col px-6 pb-6 space-y-6">
          {/* Search Input */}
          <Input
            placeholder="Search User"
            icon={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* First Section */}
          <div>
            <h3 className="text-black text-bold font-bold mb-4 uppercase">
              {primaryHeader}
            </h3>
            <div className="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
              <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                  display: none;
                }
                .custom-scrollbar {
                  -ms-overflow-style: none;
                  scrollbar-width: none;
                }
              `}</style>

              {firstSectionContent === "invited" ? (
                // Show only invited members in first section
                invitedMembers.length > 0 ? (
                  <div className="space-y-2">
                    {invitedMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg mb-3">
                        <div className="flex items-center gap-4">
                          <Avatar
                            src={member.profileImage}
                            size="large"
                            shape="circle"
                          />
                          <div className="flex flex-col">
                            <span className="text-regular font-bold text-black">
                              {member.name}
                            </span>
                            <span className="text-small text-gray">
                              {member.email}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Select
                            defaultValue={member.role || roleOptions[0]?.value}
                            style={{ width: 120 }}
                            onChange={(value) =>
                              onUpdateMember
                                ? onUpdateMember(member.id, value)
                                : null
                            }
                            options={roleOptions}
                            size="small"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray py-4">
                    {emptyInvitedMessage}
                  </div>
                )
              ) : (
                // Show all users in first section
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <div
                      key={user._id}
                      className="flex items-center justify-between p-4 bg-white rounded-lg mb-3">
                      <div className="flex items-center gap-4">
                        <Avatar
                          src={user.profileImage}
                          size="large"
                          shape="circle"
                        />
                        <div className="flex flex-col">
                          <span className="text-regular font-bold text-black">
                            {user.fullname || user.username}
                          </span>
                          <span className="text-small text-gray">
                            {user.email}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-primary text-small font-bold">
                          {user.roles && user.roles.length > 0
                            ? capitalizeRole(user.roles[0])
                            : "User"}
                        </span>
                        <Checkbox
                          checked={selectedMembers.includes(user._id)}
                          onChange={() => handleSelectMember(user._id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Button (only for all users view) */}
              {firstSectionContent === "all" && hasMoreUsers && (
                <div className="mt-4 text-center">
                  {isLoading ? (
                    <div className="text-primary">Loading more users...</div>
                  ) : (
                    <button
                      onClick={loadMoreUsers}
                      className="text-primary font-medium hover:underline py-2 px-4">
                      Load More
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Divider and Second Section (conditional) */}
          {showSecondaryHeader && (
            <>
              <div className="h-2 bg-secondary w-full"></div>
              <div>
                <h3 className="text-black text-bold font-bold mb-4 uppercase">
                  {secondaryHeader}
                </h3>
                <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {isLoading && displayedUsers.length === 0 ? (
                    <div className="text-center py-4">Loading users...</div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {filteredAvailableUsers.map((user) => (
                          <div
                            key={user._id}
                            className="flex items-center justify-between p-4 bg-white rounded-lg mb-3">
                            <div className="flex items-center gap-4">
                              <Avatar
                                src={user.profileImage}
                                size="large"
                                shape="circle"
                              />
                              <div className="flex flex-col">
                                <span className="text-regular font-bold text-black">
                                  {user.fullname || user.username}
                                </span>
                                <span className="text-small text-gray">
                                  {user.email}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-primary text-small font-bold">
                                {user.roles && user.roles.length > 0
                                  ? capitalizeRole(user.roles[0])
                                  : "User"}
                              </span>
                              <Checkbox
                                checked={selectedMembers.includes(user._id)}
                                onChange={() => handleSelectMember(user._id)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Load More Button */}
                      {hasMoreUsers && (
                        <div className="mt-4 text-center">
                          {isLoading ? (
                            <div className="text-primary">
                              Loading more users...
                            </div>
                          ) : (
                            <button
                              onClick={loadMoreUsers}
                              className="text-primary font-medium hover:underline py-2 px-4">
                              Load More
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="w-full bg-secondary p-4 flex gap-3">
          <Button
            text="CANCEL"
            color="secondary"
            className="flex-1 border border-primary"
            customBgColor="white"
            customTextColor="primary"
            onClick={onClose}
          />
          <Button
            text="PROCEED"
            color="primary"
            className="flex-1 bg-primary text-white"
            customBgColor="primary"
            customTextColor="white"
            onClick={handleProceed}
            disabled={selectedMembers.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
};

InviteMembersDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onMembersSelected: PropTypes.func,
  modalTitle: PropTypes.string,
  primaryHeader: PropTypes.string,
  showSecondaryHeader: PropTypes.bool,
  secondaryHeader: PropTypes.string,
  roleOptions: PropTypes.array,
  invitedMembers: PropTypes.array,
  emptyInvitedMessage: PropTypes.string,
  onUpdateMember: PropTypes.func,
  firstSectionContent: PropTypes.oneOf(["invited", "all"]),
};

export default InviteMembersDialog;
